import {
  AnchorButton,
  Button,
  Divider,
  FloatingTooltip,
  SelectSizeVariant,
  TooltipCore,
  UntitledLoading,
  ViewFieldset,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { format } from 'date-fns';
import { FC, useEffect } from 'react';
import { Link } from 'react-router-dom';

import useOptimisticArchiveInboundEmail from '@/api/mutations/inbound-emails/optimistic/useOptimisticArchiveInboundEmail';
import useOptimisticAssignInboundEmailToProject from '@/api/mutations/inbound-emails/optimistic/useOptimisticAssignInboundEmailToProject';
import useOptimisticMarkInboundEmailAsRead from '@/api/mutations/inbound-emails/optimistic/useOptimisticMarkInboundEmailAsRead';
import useOptimisticUnarchiveInboundEmail from '@/api/mutations/inbound-emails/optimistic/useOptimisticUnarchiveInboundEmail';
import useOptimisticUnassignInboundEmail from '@/api/mutations/inbound-emails/optimistic/useOptimisticUnassignInboundEmail';
import useGetInboundEmail from '@/api/queries/inbound-emails/useGetInboundEmail';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import ProjectSelect from '@/ui/components/projectSelect/ProjectSelect';

import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';

import MarkdownCore from '@payaca/components/markdownLabel/MarkdownCore';
import InboxEmptyState from './InboxEmptyState';

export type InboxDetailPanelProps = {
  emailId: string | null;
  onArchived: () => void;
};

const InboxDetailPanel: FC<InboxDetailPanelProps> = (props) => {
  const { emailId, onArchived } = props;

  const { data, isLoading } = useGetInboundEmail(emailId);
  const email = data?.me.inboundEmail;
  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  const { mutate: archiveEmail, isPending: isArchiving } =
    useOptimisticArchiveInboundEmail();
  const { mutate: unarchiveEmail, isPending: isUnarchiving } =
    useOptimisticUnarchiveInboundEmail();
  const { mutate: assignToProject } =
    useOptimisticAssignInboundEmailToProject();
  const { mutate: unassignEmail } = useOptimisticUnassignInboundEmail();
  const { mutate: markAsRead } = useOptimisticMarkInboundEmailAsRead();

  useEffect(() => {
    if (emailId && email?.status === 'UNREAD') {
      markAsRead({ inboundEmailId: emailId });
    }
  }, [emailId, email?.status, markAsRead]);

  if (!emailId) {
    return (
      <div className="flex flex-1 flex-col">
        <InboxEmptyState />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <UntitledLoading className="size-8" />
      </div>
    );
  }

  if (!email) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center text-gray-500">
        Email not found
      </div>
    );
  }

  const isArchived = email.status === 'ARCHIVED';

  const handleArchive = () => {
    archiveEmail(
      { inboundEmailId: emailId },
      { onSuccess: () => onArchived() }
    );
  };

  const handleUnarchive = () => {
    unarchiveEmail({ inboundEmailId: emailId });
  };

  const handleProjectChange = (
    projectPublicId?: string,
    details?: { reference: string }
  ) => {
    if (projectPublicId && details) {
      assignToProject({
        inboundEmailId: emailId,
        projectId: projectPublicId,
        projectReference: details.reference,
      });
    } else {
      unassignEmail({ inboundEmailId: emailId });
    }
  };

  const isForwardedEmail = email.addressType === 'FORWARD';
  const fromText = email.fromName
    ? `${email.fromName} <${email.fromEmail}>`
    : email.fromEmail;

  const fromDisplay = isForwardedEmail
    ? (email.originalFrom ?? email.fromEmail)
    : fromText;

  // For auto-forwards the FROM header is the original sender, so there's no
  // meaningful "via" to show — only render the Via line when it differs.
  const isManualForward =
    isForwardedEmail &&
    !!email.originalFrom &&
    email.originalFrom !== email.fromEmail;
  const viaToDisplay = isForwardedEmail
    ? isManualForward
      ? fromText
      : null
    : email.toEmails.join(', ');

  return (
    <div className="flex flex-1 flex-col typography:prose">
      <div className="flex items-center justify-between border-b p-4">
        {isArchived ? (
          <FloatingTooltip>
            <Button
              variant="white"
              size="sm"
              colour="black"
              onClick={handleUnarchive}
              isProcessing={isUnarchiving}
              className="h-full aspect-square"
            >
              <UntitledIcon name="box.3" className="size-4" />
              <span className="sr-only">Unarchive</span>
            </Button>

            <TooltipCore>Unarchive</TooltipCore>
          </FloatingTooltip>
        ) : (
          <FloatingTooltip>
            <Button
              variant="white"
              size="sm"
              colour="black"
              onClick={handleArchive}
              isProcessing={isArchiving}
              className="h-full aspect-square"
            >
              <UntitledIcon name="box.3" className="size-4" />
              <span className="sr-only">Archive</span>
            </Button>

            <TooltipCore>Archive</TooltipCore>
          </FloatingTooltip>
        )}
        <FloatingTooltip>
          <AnchorButton
            as={Link}
            variant="white"
            size="sm"
            colour="black"
            className="h-full aspect-square"
            to="/inbox"
          >
            <UntitledIcon name="x-close" className="size-3" />
            <span className="sr-only">Close email</span>
          </AnchorButton>

          <TooltipCore>Close email</TooltipCore>
        </FloatingTooltip>
      </div>
      <div className="flex-1 overflow-y-auto p-6 @container">
        <h3 className="mb-4">{email.subject}</h3>
        <ViewFieldset className="@md:grid-cols-2">
          <ViewFieldset.Field label="From">{fromDisplay}</ViewFieldset.Field>
          {viaToDisplay && (
            <ViewFieldset.Field label={isForwardedEmail ? 'Via' : 'To'}>
              {viaToDisplay}
            </ViewFieldset.Field>
          )}
          <ViewFieldset.Field label="Date" className="border-none">
            {format(
              new Date(email.createdAt),
              getInternationalDateFormatByLocale(
                DateFormats.TIMESTAMP,
                accountRegionPreferences?.locale
              )
            )}
          </ViewFieldset.Field>
          <ViewFieldset.Field label="Project">
            <div className="flex gap-2.5">
              <ProjectSelect
                projectPublicId={email.assignedProject?.publicId}
                onChange={handleProjectChange}
                sizeVariant={SelectSizeVariant.SM}
              />
              {!!email.assignedProject && (
                <FloatingTooltip>
                  <Button
                    variant="white"
                    size="sm"
                    colour="black"
                    onClick={() => handleProjectChange()}
                  >
                    <UntitledIcon name="link-broken-01.3" className="size-4" />
                    <div className="sr-only">Unassign project</div>
                  </Button>

                  <TooltipCore>Unassign project</TooltipCore>
                </FloatingTooltip>
              )}
            </div>
          </ViewFieldset.Field>
        </ViewFieldset>

        <Divider className="my-3" />

        {/* v1: render plain text body only. HTML body rendering deferred to a future iteration. */}
        <div className="typography:prose">
          {email.textBody && <MarkdownCore markdown={email.textBody} />}
        </div>
      </div>
    </div>
  );
};

export default InboxDetailPanel;
